import { AxiosError } from 'axios';
import navigationService from '../services/navigationService';

/**
 * Standard API error response interface
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, any>;
  status?: number;
}

/**
 * Handles API errors from Axios and returns a standardized error object
 */
export function handleApiError(error: unknown): ApiError {
  // Handle Axios errors
  if (error instanceof AxiosError) {
    const { response, request, message: axiosMessage } = error;

    // Server responded with an error status
    if (response) {
      const { status, data } = response;
      
      // Handle auth errors by redirecting
      if (status === 401) {
        navigationService.redirectToLogin();
      } else if (status === 403) {
        navigationService.redirectToUnauthorized();
      }
      
      // If the server already returned a structured error
      if (data && (data.message || data.error)) {
        return {
          code: data.code || `ERROR_${status}`,
          message: data.message || data.error || 'An error occurred',
          details: data.details || data,
          status,
        };
      }

      // Generic error based on status code
      return {
        code: `ERROR_${status}`,
        message: getErrorMessageByStatus(status),
        details: data,
        status,
      };
    } 
    
    // Network error (no response received)
    if (request) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection and try again.',
        details: { originalError: error },
      };
    }

    // Other Axios errors
    return {
      code: 'REQUEST_SETUP_ERROR',
      message: axiosMessage || 'Error setting up the request',
      details: { originalError: error },
    };
  }

  // Handle other types of errors
  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unknown error occurred',
      details: { originalError: error },
    };
  }

  // Handle non-Error objects
  return {
    code: 'UNKNOWN_ERROR',
    message: typeof error === 'string' ? error : 'An unknown error occurred',
    details: { originalError: error },
  };
}

/**
 * Formats an error message for user display
 */
export function formatErrorMessage(error: ApiError | unknown): string {
  if (!error) {
    return 'An unknown error occurred';
  }

  // If it's already an ApiError
  if (typeof error === 'object' && error !== null && 'code' in error && 'message' in error) {
    return (error as ApiError).message;
  }

  // If it's an Error instance
  if (error instanceof Error) {
    return handleApiError(error).message;
  }

  // Handle string error
  if (typeof error === 'string') {
    return error;
  }

  // Default fallback for other unknown types
  return 'An unknown error occurred';
}

/**
 * Returns appropriate error message based on HTTP status code
 */
export function getErrorMessageByStatus(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your data and try again.';
    case 401:
      return 'Authentication required. Please log in and try again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'Conflict with the current state of the resource.';
    case 422:
      return 'Validation error. Please check your input and try again.';
    case 429:
      return 'Too many requests. Please wait and try again later.';
    case 500:
      return 'Server error. Please try again later or contact support.';
    case 503:
      return 'Service unavailable. Please try again later.';
    default:
      return `Error ${status}. Please try again later or contact support.`;
  }
}

/**
 * Handles HTTP errors with appropriate actions
 */
export function handleHttpError(status: number): void {
  switch (status) {
    case 401:
      navigationService.redirectToLogin();
      break;
    case 403:
      navigationService.redirectToUnauthorized();
      break;
  }
}

export default {
  handleApiError,
  formatErrorMessage,
  handleHttpError,
}; 